import { Injectable } from "@angular/core";

/** Margen antes de dar por vacío un hueco que GAM nunca llegó a contestar. */
const ESPERA_RESPUESTA_MS = 8000;

/** Agrupa las mutaciones del DOM del scroll infinito en una sola pasada. */
const DEBOUNCE_MS = 150;

/**
 * Decide qué huecos de publicidad de la portada móvil deben ocupar sitio.
 *
 * Los divs (#publi-movil-N) los rellena GPT desde GTM, así que no se pueden quitar con un
 * *ngIf: googletag.display() necesita encontrarlos. La altura la sigue poniendo el CSS, con
 * la caja fija de 350px de siempre; lo único que hace este servicio es repartir tres clases:
 *
 *   .publi-con-slot -> el id corresponde a una posición dada de alta en GAM, así que
 *                      reserva sus 350px y se comporta como ha funcionado siempre.
 *   .publi-sin-slot -> no hay tal posición. La portada genera un divisor por bloque y el
 *                      scroll infinito no tiene techo, así que pasado el último slot
 *                      quedaban huecos que nadie iba a rellenar jamás.
 *   .publi-vacia    -> la posición existe pero GAM contestó sin creatividad, o no contestó
 *                      en todo el rato. Se colapsa.
 *
 * Las posiciones se leen de googletag en tiempo de ejecución, no se codifican aquí: si
 * mañana se dan de alta más divisores en GAM, se pintan solos sin tocar este fichero.
 */
@Injectable({ providedIn: "root" })
export class GamSlotsService {
  private iniciado = false;
  private gptListo = false;
  private temporizador: any = null;
  private temporizadorColapso: any = null;

  /** Ids que ya han pasado por slotRenderEnded, con o sin creatividad. */
  private readonly respondidos = new Set<string>();

  init(): void {
    if (this.iniciado || typeof window === "undefined") return;
    this.iniciado = true;

    const w = window as any;
    w.googletag = w.googletag || { cmd: [] };

    w.googletag.cmd.push(() => {
      // Todo va en try/catch: esto corre dentro de la cola de GPT y una excepción aquí
      // podría arrastrar lo que GPT tenga encolado después. Si algo falla, no se colapsa
      // ningún hueco, pero los anuncios se piden y se pintan igual.
      try {
        this.gptListo = true;

        w.googletag.pubads().addEventListener("slotRenderEnded", (evento: any) => {
          const id = evento.slot.getSlotElementId();
          this.respondidos.add(id);

          const slot = document.getElementById(id);
          if (!slot) return;

          // Para los ROBA el hueco lo marca el wrapper; para los divisores es el propio div.
          const caja = (slot.closest(".publi-container, .contenedor-publicidad") as HTMLElement) || slot;
          caja.classList.toggle("publi-vacia", !!evento.isEmpty);

          // Si algo ha renderizado, las posiciones ya están declaradas: buen momento para
          // repasar los divisores que en la primera pasada no se pudieron evaluar.
          this.programarSincronizacion();
        });

        this.sincronizarDivisores();
      } catch (e) {
        console.warn("[GamSlots] no se pudieron colapsar los huecos", e);
      }
    });

    // El scroll infinito añade divisores después del primer render: hay que repasarlos.
    this.observarNuevosDivisores();

  }

  /** Separa los divisores que tienen posición en GAM de los que no. */
  private sincronizarDivisores(): void {
    if (!this.gptListo) return;

    const w = window as any;
    const slots: any[] = w.googletag.pubads().getSlots() || [];

    // GPT puede estar listo y todavía no haber corrido el tag de GTM que declara las
    // posiciones. Con la lista vacía no se puede decidir nada: marcarlo todo dejaría la
    // portada sin un solo anuncio, así que se espera a una pasada posterior.
    if (slots.length === 0) return;

    const definidos = new Set<string>(slots.map((s) => s.getSlotElementId()));

    document.querySelectorAll<HTMLElement>(".publi-divisor-movil").forEach((el) => {
      const tiene = definidos.has(el.id);
      el.classList.toggle("publi-con-slot", tiene);
      el.classList.toggle("publi-sin-slot", !tiene);
    });

    this.programarColapso();
  }

  /**
   * Un hueco que reserva sus 350px y nunca recibe respuesta se quedaría ocupando sitio para
   * siempre. Pasado un rato sin actividad se da por vacío; si GAM contesta más tarde, el
   * propio slotRenderEnded le quita la clase y vuelve a aparecer.
   */
  private programarColapso(): void {
    clearTimeout(this.temporizadorColapso);
    this.temporizadorColapso = setTimeout(() => {
      document.querySelectorAll<HTMLElement>(".publi-divisor-movil").forEach((el) => {
        if (el.classList.contains("publi-sin-slot") || this.respondidos.has(el.id)) return;
        el.classList.add("publi-vacia");
      });
    }, ESPERA_RESPUESTA_MS);
  }

  /** Agrupa varias llamadas seguidas en una sola pasada. */
  private programarSincronizacion(): void {
    clearTimeout(this.temporizador);
    this.temporizador = setTimeout(() => this.sincronizarDivisores(), DEBOUNCE_MS);
  }

  private observarNuevosDivisores(): void {
    if (typeof MutationObserver === "undefined") return;

    const observador = new MutationObserver((mutaciones) => {
      const hayDivisores = mutaciones.some((m) => Array.from(m.addedNodes).some((n) => n instanceof HTMLElement && (n.classList.contains("publi-divisor-movil") || !!n.querySelector?.(".publi-divisor-movil"))));
      if (!hayDivisores) return;

      this.programarSincronizacion();
    });

    observador.observe(document.body, { childList: true, subtree: true });
  }
}
